import React, { useState, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { Flex, Button, toast } from "@jsq/ds/core";
import { Colors, Sizes } from "@jsq/ds/styles";
import { DataStudioJobSubstateEnum, DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import { useCreateOnboardingJobStepMutation } from "~/graphql/supergraphHooks";
import { DATA_STUDIO_DASHBOARD_ROUTE, DataStudioEnvironment } from "../../constants";
import { useCrmImportJobDetails } from "../../hooks";
import { useIdQueryParam } from "../../hooks/useIdQueryParam";
import LoadingOverlay from "../LoadingOverlay";
import * as sharedStyles from "../shared/styles_vanilla.css";
import DataFilesEntityChoices from "./DataFilesEntityChoices";
import ProjectSetupConfiguration from "./ProjectSetupConfiguration";
import * as styles from "./styles_vanilla.css";
import {
  transformJobToProjectSetup,
  transformJobToFiles,
  calculateFileSummary,
  isValidJobData,
  transformJobConfigForSubmission,
  getComponentState,
} from "./utils";

const TestDataRelease: React.FC = () => {
  const history = useHistory();
  const { idFromUrl } = useIdQueryParam();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: job,
    loading: jobLoading,
    error: jobError,
    refetch: refetchJobDetails,
  } = useCrmImportJobDetails(idFromUrl || "", {
    skip: !idFromUrl,
  });

  const [createJobStep] = useCreateOnboardingJobStepMutation({
    onCompleted: () => {
      toast.success("Test data release scheduled successfully!");
      history.push(DATA_STUDIO_DASHBOARD_ROUTE);
    },
    onError: (error: ApolloError) => {
      toast.error(`Failed to schedule release: ${error.message}`);
    },
  });

  useEffect(() => {
    if (jobError) {
      toast.error(`Error loading job data: ${jobError.message}`);
    } else if (!idFromUrl) {
      toast.error("No job ID found in URL.");
    } else if (!jobLoading && (!job || !isValidJobData(job))) {
      toast.error("Job not found or invalid job data.");
    }
  }, [jobError, idFromUrl, jobLoading, job]);

  const componentState = getComponentState(
    idFromUrl,
    jobLoading,
    jobError,
    job,
    isValidJobData,
  );

  const handleScheduleRelease = async () => {
    if (!job || !isValidJobData(job) || isSubmitting) {
      if (!job || !isValidJobData(job)) {
        toast.error("Invalid job data. Cannot schedule release.");
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const jobConfig = transformJobConfigForSubmission(job);

      await createJobStep({
        variables: {
          input: {
            jobType: DataStudioJobTypeEnum.CrmImport,
            arenaDomain: job.arenaDomain,
            arenaUserEmail: job.arenaUserEmail,
            env: DataStudioEnvironment.TEST,
            jobId: job.jobId,
            jobConfig,
            substate: DataStudioJobSubstateEnum.ScheduleImport,
            createdAt: job.createdAt,
          },
        },
      });
    } catch {
      // Error handling is done in onError callback
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (componentState.type === "success" && job && isValidJobData(job)) {
      const projectSetup = transformJobToProjectSetup(job);
      const files = transformJobToFiles(job);
      const fileSummary = calculateFileSummary(files);

      return (
        <Flex flexDirection="column" gap={Sizes.LG} className={styles.mainContainer}>
          <Flex flexDirection="column" gap={Sizes.LG} className={sharedStyles.stageContainer}>
            <ProjectSetupConfiguration projectSetup={projectSetup} />
          </Flex>

          <DataFilesEntityChoices files={files} fileSummary={fileSummary} />

          <Flex justifyContent="flex-end">
            <Button
              variant="custom"
              backgroundColor={Colors.Neutral900}
              textColor={Colors.Neutral0}
              onClick={handleScheduleRelease}
              disabled={isSubmitting}
              className={sharedStyles.navigationButton}
            >
              {isSubmitting ? "Scheduling..." : "Schedule Release"}
            </Button>
          </Flex>
        </Flex>
      );
    }

    return null;
  };

  const shouldShowErrorOverlay =
    componentState.type === "error" && componentState.title !== "Job Not Found";

  return (
    <LoadingOverlay
      loading={componentState.type === "loading"}
      error={shouldShowErrorOverlay ? new Error(componentState.message) : null}
      message="Loading job details..."
      onRetry={
        shouldShowErrorOverlay && componentState.showRetry
          ? () => {
              refetchJobDetails();
            }
          : undefined
      }
      onCancel={() => history.push(DATA_STUDIO_DASHBOARD_ROUTE)}
      cancelText="Back to Data Studio"
    >
      {renderContent()}
    </LoadingOverlay>
  );
};

export default TestDataRelease;
